import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import AuthService from '../../services/AuthService';
import { API_CONFIG } from '../../utils/constants';

const RegisterModal = ({ onClose, onSwitchToLogin }) => {
  const { authStart, authSuccess, authError } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    password: '', confirmPassword: '', acceptTerms: false
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const googleButtonRef = useRef(null);
  const errorClasses = "text-red-500 text-[10px] mt-1 font-bold uppercase tracking-wide pl-1";

  useEffect(() => {
    const initializeGoogle = () => {
      if (window.google && API_CONFIG.GOOGLE_CLIENT_ID) {
        try {
          window.google.accounts.id.initialize({
            client_id: API_CONFIG.GOOGLE_CLIENT_ID,
            callback: handleGoogleCallback,
            auto_select: false,
            cancel_on_tap_outside: true,
          });
          if (googleButtonRef.current) {
            window.google.accounts.id.renderButton(googleButtonRef.current, {
              theme: 'filled_black', size: 'large', width: '100%', text: 'signup_with', shape: 'pill',
            });
          }
        } catch (error) { console.error('Google Sign-Up initialization error:', error); }
      }
    };
    if (window.google) initializeGoogle();
    else {
      const checkGoogle = setInterval(() => { if (window.google) { clearInterval(checkGoogle); initializeGoogle(); } }, 100);
      setTimeout(() => clearInterval(checkGoogle), 10000);
    }
  }, []);

  const handleGoogleCallback = async (response) => {
    if (!response.credential) return;
    setGoogleLoading(true); setApiError(''); authStart();
    try {
      const result = await AuthService.googleLogin(response.credential);
      authSuccess(result.user, result.access_token, result.refresh_token);
      onClose();
    } catch (error) { setApiError(error.message || 'Google Sign-Up failed'); authError(error.message || 'Google Sign-Up failed'); }
    finally { setGoogleLoading(false); }
  };

  const validateField = (name, value) => {
    if (!value && name !== 'acceptTerms') return 'Required';
    if (name === 'email' && !/\S+@\S+\.\S+/.test(value)) return 'Invalid email';
    if (name === 'phone' && !/^\+\d{10,15}$/.test(value.replace(/[\s\-()]/g, ''))) return 'Phone must start with + and have 10-15 digits. Example: +94771234567';
    if (name === 'password' && value.length < 8) return 'Min 8 characters';
    if (name === 'confirmPassword' && value !== formData.password) return 'Mismatch';
    if (name === 'acceptTerms' && !value) return 'Required';
    return '';
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (touched[name]) setErrors(prev => ({ ...prev, [name]: validateField(name, newValue) }));
  };

  const handleBlur = (e) => {
    const { name, value, type, checked } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, type === 'checkbox' ? checked : value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    Object.keys(formData).forEach(k => { const err = validateField(k, formData[k]); if (err) newErrors[k] = err; });
    setErrors(newErrors); setTouched(Object.keys(formData).reduce((a, k) => ({ ...a, [k]: true }), {}));
    if (Object.keys(newErrors).length > 0) return;
    setIsLoading(true); setApiError(''); authStart();
    try {
      await AuthService.register({ ...formData, email: formData.email.toLowerCase(), phone: formData.phone.replace(/[\s\-()]/g, '') });
      setRegistrationSuccess(true);
    } catch (error) { setApiError(error.message || 'Registration failed'); authError(error.message || 'Registration failed'); }
    finally { setIsLoading(false); }
  };

  if (registrationSuccess) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="glass-panel max-w-md w-full rounded-[2.5rem] p-10 text-center border-white/10 shadow-2xl animate-in zoom-in duration-300">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg shadow-green-500/20">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h2 className="text-3xl font-black text-white tracking-tighter uppercase mb-4">Verification Sent</h2>
          <p className="text-gray-400 text-sm font-medium mb-10 leading-relaxed">We've dispatched a secure link to <strong className="text-blue-500">{formData.email}</strong>. Please authenticate to activate your account.</p>
          <button onClick={onClose} className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-500/20 active:scale-95 transition-all">Got It</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-panel max-w-lg w-full rounded-[2.5rem] overflow-hidden border-white/10 shadow-2xl animate-in fade-in zoom-in duration-300 max-h-[90vh] overflow-y-auto">
        <div className="p-8 pb-4 flex items-center justify-between sticky top-0 bg-[#0a0f18]/90 backdrop-blur-md z-10">
          <div>
            <h2 className="text-2xl font-black text-white tracking-tighter uppercase">Join Network</h2>
            <p className="text-[10px] text-blue-500 font-black uppercase tracking-widest mt-1">Premium Chauffeur Access</p>
          </div>
          <button onClick={onClose} className="p-2 bg-white/5 border border-white/10 rounded-full text-white hover:bg-white/10 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="p-8 pt-4">
          {apiError && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
              <svg className="w-5 h-5 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <p className="text-xs text-red-500 font-bold">{apiError}</p>
            </div>
          )}

          <div className="mb-6"><div ref={googleButtonRef} /></div>
          
          <div className="relative mb-8 text-center">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/5"></div></div>
            <span className="relative px-3 bg-[#0a0f18] text-[9px] text-gray-500 font-black uppercase tracking-widest">Or create profile</span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1.5 ml-1 block">First Name</label>
                <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} onBlur={handleBlur} className={`w-full bg-white/5 border rounded-xl px-4 py-2.5 text-white text-sm focus:ring-1 focus:ring-blue-500 outline-none transition-all ${errors.firstName ? 'border-red-500/50' : 'border-white/10'}`} placeholder="John" />
                {errors.firstName && <p className={errorClasses}>{errors.firstName}</p>}
              </div>
              <div>
                <label className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1.5 ml-1 block">Last Name</label>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} onBlur={handleBlur} className={`w-full bg-white/5 border rounded-xl px-4 py-2.5 text-white text-sm focus:ring-1 focus:ring-blue-500 outline-none transition-all ${errors.lastName ? 'border-red-500/50' : 'border-white/10'}`} placeholder="Doe" />
                {errors.lastName && <p className={errorClasses}>{errors.lastName}</p>}
              </div>
            </div>

            <div>
              <label className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1.5 ml-1 block">Email Address</label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} onBlur={handleBlur} className={`w-full bg-white/5 border rounded-xl px-4 py-2.5 text-white text-sm focus:ring-1 focus:ring-blue-500 outline-none transition-all ${errors.email ? 'border-red-500/50' : 'border-white/10'}`} placeholder="john@example.com" />
              {errors.email && <p className={errorClasses}>{errors.email}</p>}
            </div>

            <div>
              <label className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1.5 ml-1 block">Phone Number</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} onBlur={handleBlur} className={`w-full bg-white/5 border rounded-xl px-4 py-2.5 text-white text-sm focus:ring-1 focus:ring-blue-500 outline-none transition-all ${errors.phone ? 'border-red-500/50' : 'border-white/10'}`} placeholder="+94 77 123 4567" />
              {errors.phone && <p className={errorClasses}>{errors.phone}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1.5 ml-1 block">Password</label>
                <input type="password" name="password" value={formData.password} onChange={handleInputChange} onBlur={handleBlur} className={`w-full bg-white/5 border rounded-xl px-4 py-2.5 text-white text-sm focus:ring-1 focus:ring-blue-500 outline-none transition-all ${errors.password ? 'border-red-500/50' : 'border-white/10'}`} placeholder="••••••••" />
                {errors.password && <p className={errorClasses}>{errors.password}</p>}
              </div>
              <div>
                <label className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1.5 ml-1 block">Confirm</label>
                <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} onBlur={handleBlur} className={`w-full bg-white/5 border rounded-xl px-4 py-2.5 text-white text-sm focus:ring-1 focus:ring-blue-500 outline-none transition-all ${errors.confirmPassword ? 'border-red-500/50' : 'border-white/10'}`} placeholder="••••••••" />
                {errors.confirmPassword && <p className={errorClasses}>{errors.confirmPassword}</p>}
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer group mt-4">
              <input type="checkbox" name="acceptTerms" checked={formData.acceptTerms} onChange={handleInputChange} className="hidden" />
              <div className="flex flex-col gap-1">
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center transition-all ${formData.acceptTerms ? 'bg-blue-600 border-blue-600' : errors.acceptTerms ? 'border-red-500' : 'border-white/20'}`}>
                    {formData.acceptTerms && <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" /></svg>}
                  </div>
                  <span className="text-[10px] text-gray-400 font-bold leading-relaxed group-hover:text-gray-200">I accept the high-standard <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-500">terms of service</a> and premium <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-500">privacy agreement</a> of LankaWayFinder.</span>
                </div>
                {errors.acceptTerms && <p className={errorClasses}>{errors.acceptTerms}</p>}
              </div>
            </label>

            <button type="submit" disabled={isLoading} className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-500/20 active:scale-95 transition-all mt-6 disabled:opacity-50">
              {isLoading ? 'Creating Profile...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-8 text-center pt-6 border-t border-white/5">
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Already a member? <button onClick={onSwitchToLogin} className="text-blue-500 hover:text-white">Sign In</button></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterModal;